import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '../../environments/environment';

export interface B2bPortalUser {
  userId: number;
  name: string;
  email: string;
  active: boolean;
  customerId: number;
  b2BContractId: number;
  contractNumber?: string | null;
  contractName?: string | null;
  roleCode?: string | null;
  roleName?: string | null;
  permissions: string[];
  modules: string[];
  lastLoginAt?: string | null;
}

export interface B2bPortalRole {
  roleId: number;
  code: string;
  name: string;
  description?: string | null;
  isSystem: boolean;
  permissionCodes: string[];
  modules: string[];
  userCount: number;
}

export interface CreateB2bPortalRolePayload {
  code: string;
  name: string;
  description?: string;
  permissionCodes?: string[];
}

export interface B2bPortalPermission {
  permissionId: number;
  code: string;
  name: string;
  module: string;
  description?: string | null;
}

export interface CreateB2bPortalUserPayload {
  name: string;
  email: string;
  password: string;
  b2BContractId: number;
  roleCode?: string;
}

export interface UpdateB2bPortalUserPayload {
  name?: string;
  roleCode?: string;
  active?: boolean;
  password?: string;
}

@Injectable({ providedIn: 'root' })
export class B2bPortalUsersService {
  private base = (environment.microservices as any).b2bAuth as string;

  constructor(private http: HttpClient) {}

  listByContract(contractId: number, page = 1, pageSize = 20, search = ''): Observable<{
    data: B2bPortalUser[];
    totalItems: number;
    currentPage: number;
    totalPages: number;
    pageSize: number;
  }> {
    let params = new HttpParams()
      .set('page', String(page))
      .set('pageSize', String(pageSize));
    if (search?.trim()) params = params.set('search', search.trim());

    return this.http.get<any>(`${this.base}/b2b-auth/contracts/${contractId}/users`, { params }).pipe(
      map(res => {
        const data = (res?.data ?? res?.Data ?? []).map((u: any) => this.normalizeUser(u));
        const pag = res?.pagination ?? res?.Pagination ?? {};
        return {
          data,
          totalItems: pag.totalItems ?? pag.TotalItems ?? data.length,
          currentPage: pag.currentPage ?? pag.CurrentPage ?? page,
          totalPages: pag.totalPages ?? pag.TotalPages ?? 1,
          pageSize: pag.pageSize ?? pag.PageSize ?? pageSize,
        };
      })
    );
  }

  getById(userId: number): Observable<B2bPortalUser> {
    return this.http.get<any>(`${this.base}/b2b-auth/users/${userId}`).pipe(
      map(res => this.normalizeUser(res?.user ?? res?.User ?? res))
    );
  }

  create(payload: CreateB2bPortalUserPayload): Observable<B2bPortalUser> {
    return this.http.post<any>(`${this.base}/b2b-auth/users`, payload).pipe(
      map(res => this.normalizeUser(res?.user ?? res?.User ?? res))
    );
  }

  update(userId: number, payload: UpdateB2bPortalUserPayload): Observable<B2bPortalUser> {
    return this.http.put<any>(`${this.base}/b2b-auth/users/${userId}`, payload).pipe(
      map(res => this.normalizeUser(res?.user ?? res?.User ?? res))
    );
  }

  setActive(userId: number, active: boolean): Observable<B2bPortalUser> {
    return this.http.put<any>(`${this.base}/b2b-auth/users/${userId}/active`, { active }).pipe(
      map(res => this.normalizeUser(res?.user ?? res?.User ?? res))
    );
  }

  delete(userId: number): Observable<void> {
    return this.http.delete<any>(`${this.base}/b2b-auth/users/${userId}`).pipe(map(() => void 0));
  }

  listRoles(): Observable<B2bPortalRole[]> {
    return this.http.get<any>(`${this.base}/b2b-auth/roles`).pipe(
      map(res => (res?.data ?? res?.Data ?? []).map((r: any) => this.normalizeRole(r)))
    );
  }

  listPermissions(): Observable<B2bPortalPermission[]> {
    return this.http.get<any>(`${this.base}/b2b-auth/permissions`).pipe(
      map(res => (res?.data ?? res?.Data ?? []).map((p: any) => ({
        permissionId: p.permissionId ?? p.PermissionId,
        code: p.code ?? p.Code,
        name: p.name ?? p.Name,
        module: p.module ?? p.Module ?? '',
        description: p.description ?? p.Description ?? null,
      })))
    );
  }

  updateRolePermissions(roleCode: string, permissionCodes: string[]): Observable<B2bPortalRole> {
    return this.http.put<any>(`${this.base}/b2b-auth/roles/${encodeURIComponent(roleCode)}/permissions`, {
      permissionCodes
    }).pipe(map(res => this.normalizeRole(res?.role ?? res?.Role ?? res)));
  }

  createRole(payload: CreateB2bPortalRolePayload): Observable<B2bPortalRole> {
    return this.http.post<any>(`${this.base}/b2b-auth/roles`, payload).pipe(
      map(res => this.normalizeRole(res?.role ?? res?.Role ?? res))
    );
  }

  deleteRole(roleCode: string): Observable<void> {
    return this.http.delete<any>(`${this.base}/b2b-auth/roles/${encodeURIComponent(roleCode)}`).pipe(
      map(() => void 0)
    );
  }

  seed(): Observable<void> {
    return this.http.post<any>(`${this.base}/b2b-auth/seed`, {}).pipe(map(() => void 0));
  }

  private normalizeUser(u: any): B2bPortalUser {
    return {
      userId: u.userId ?? u.UserId,
      name: u.name ?? u.Name ?? '',
      email: u.email ?? u.Email ?? '',
      active: u.active ?? u.Active ?? false,
      customerId: u.customerId ?? u.CustomerId,
      b2BContractId: u.b2BContractId ?? u.B2BContractId,
      contractNumber: u.contractNumber ?? u.ContractNumber ?? null,
      contractName: u.contractName ?? u.ContractName ?? null,
      roleCode: u.roleCode ?? u.RoleCode ?? null,
      roleName: u.roleName ?? u.RoleName ?? null,
      permissions: u.permissions ?? u.Permissions ?? [],
      modules: u.modules ?? u.Modules ?? [],
      lastLoginAt: u.lastLoginAt ?? u.LastLoginAt ?? null,
    };
  }

  private normalizeRole(r: any): B2bPortalRole {
    return {
      roleId: r.roleId ?? r.RoleId,
      code: r.code ?? r.Code,
      name: r.name ?? r.Name,
      description: r.description ?? r.Description ?? null,
      isSystem: !!(r.isSystem ?? r.IsSystem),
      permissionCodes: r.permissionCodes ?? r.PermissionCodes ?? [],
      modules: r.modules ?? r.Modules ?? [],
      userCount: r.userCount ?? r.UserCount ?? 0,
    };
  }
}
